/**
 * ลำดับความอาวุโสของระดับตำแหน่ง — ใช้ร่วมกันทั้งเซิร์ฟเวอร์และหน้าจอ
 *
 * ⚠ แยกออกมาจาก category.ts โดยตั้งใจ เพื่อให้ฝั่ง client import ได้ (ดู alias @shared/levels)
 *   ไฟล์นี้ต้องไม่ import อะไรเลย — ไม่มีชนิด ไม่มีโมดูลอื่น
 *   ถ้าเผลอ import อะไรที่ลากโค้ดเซิร์ฟเวอร์ตามมา มันจะไปโผล่ในบันเดิลของเบราว์เซอร์
 *   (เหตุผลเดียวกับที่ views.ts เป็นชนิดล้วน ๆ)
 *
 * ⚠ เรียงจาก "สูงสุด" ลงไป เลขน้อย = อาวุโสกว่า
 *   ระดับที่ไม่รู้จักได้ 99 คือไปอยู่ท้ายสุด ไม่ใช่ต้นสุด — ของที่ระบบอ่านไม่ออก
 *   ไม่ควรถูกดันขึ้นไปอยู่หัวรายการผู้บริหาร
 */
export const LEVEL_ORDER = [
  'บริหารสูง', 'บริหารต้น', 'อำนวยการสูง', 'อำนวยการต้น',
  'ทรงคุณวุฒิ', 'เชี่ยวชาญ', 'ชำนาญการพิเศษ', 'ชำนาญการ', 'ปฏิบัติการ',
  'ทักษะพิเศษ', 'อาวุโส', 'ชำนาญงาน', 'ปฏิบัติงาน',
  'กลุ่มงานวิชาชีพเฉพาะ', 'กลุ่มงานเชี่ยวชาญเฉพาะ', 'กลุ่มงานบริหารทั่วไป',
  'กลุ่มงานเทคนิค', 'กลุ่มงานเทคนิคทั่วไป', 'กลุ่มงานบริการ',
  'ระดับ ส3', 'ระดับ ส2', 'ระดับ ช3', 'ระดับ บ2', 'ลูกจ้างชั่วคราว',
];

export function levelRank(level: string): number {
  const i = LEVEL_ORDER.indexOf(level.trim());
  return i === -1 ? 99 : i;
}

/**
 * ระดับตาม พ.ร.บ. 2551 → "ซี" ของระบบเดิม
 *
 * ผู้บริหารส่วนใหญ่ยังพูดเป็นซี ("ตำแหน่งซี 9 ว่างกี่อัตรา") ไม่ได้พูดว่า "อำนวยการสูง"
 * รายการอัตราจึงควรบอกซีกำกับไว้ให้อ่านออกโดยไม่ต้องแปลในหัว
 *
 * ⚠ ตารางนี้มาจากตารางเทียบระดับตาม พ.ร.บ. 2551 กับซีเดิมทั้ง 13 ระดับ
 *   ไม่ได้เดาเพิ่มเอง ระดับที่ไม่มีการเทียบที่อ้างอิงได้จะไม่อยู่ในตารางนี้
 *   และต้อง "ไม่แสดงซี" ไม่ใช่เดาให้ — การเดาซีผิดหนึ่งขั้นคือการบอกสิทธิ์
 *   ในการแต่งตั้งผิดไปทั้งขั้น ซึ่งร้ายกว่าการไม่บอกอะไรเลย
 *
 * ⚠ ก่อน 14 ก.ย. 2569 ตารางนี้มีแค่ซี 8 ขึ้นไป เพราะลอกมาจากคำอธิบายของ C9_UP_LEVELS
 *   ซึ่งเป็นชุดที่เขียนไว้เพื่อมุมมองปลัดโดยเฉพาะ ไม่ใช่ตารางเทียบที่ครบทั้งระบบ
 *   ผลคือ 2,801 อัตราครึ่งล่างของกระทรวงไม่มีป้ายซีเลยทั้งที่เทียบได้
 *
 * ⚠ ค่าที่เป็นช่วง (ซี 6–7 · ซี 3–5 · ซี 1–4 · ซี 5–6 · ซี 7–8) เป็นช่วงจริง
 *   ไม่ใช่การเลี่ยงตอบ — ระบบเก่าแยกสองซีในแท่งเดียวกันด้วยขั้นเงินเดือนและผลประเมิน
 *   ซึ่งเป็นข้อมูลที่หายไปตอน พ.ร.บ. 2551 ยุบแท่ง จึงคำนวณย้อนไม่ได้จากฟิลด์ที่มี
 *   ⚠ ป้ายที่เป็นช่วงใช้ได้เฉพาะกับ "หัวกลุ่มระดับ" เท่านั้น
 *     ห้ามเอาไปติดข้างชื่อคนหรืออัตราเดี่ยว เพราะไม่มีใครมีสองซีพร้อมกัน
 *
 * ⚠ พนักงานราชการ (กลุ่มงาน…) และลูกจ้าง ไม่มีการเทียบซี — ว่างไว้โดยตั้งใจ ไม่ใช่ตกหล่น
 */
const C_GRADE: Record<string, string> = {
  บริหารสูง: 'ซี 10–11',
  ทรงคุณวุฒิ: 'ซี 10–11',
  บริหารต้น: 'ซี 9',
  อำนวยการสูง: 'ซี 9',
  เชี่ยวชาญ: 'ซี 9',
  อำนวยการต้น: 'ซี 8',
  ชำนาญการพิเศษ: 'ซี 8',
  ชำนาญการ: 'ซี 6–7',
  ปฏิบัติการ: 'ซี 3–5',
  ทักษะพิเศษ: 'ซี 9',
  อาวุโส: 'ซี 7–8',
  ชำนาญงาน: 'ซี 5–6',
  ปฏิบัติงาน: 'ซี 1–4',
};

/** ซีที่เทียบได้ของระดับหนึ่ง — null เมื่อไม่มีการเทียบที่อ้างอิงได้ */
export function cGradeOf(level: string | null | undefined): string | null {
  return C_GRADE[(level ?? '').trim()] ?? null;
}
